'use client';
import { ChangeEvent, SyntheticEvent, useMemo } from 'react';
import Image from 'next/image';

import SubmitIcon from 'public/img/icon/arrow-elbow-down-left.svg';
import './EllaChatInput.style.scss';

import { useEllaAnalytics } from '@/components/hook/analytics';
import analytics from '@/util/analytics';

type EllaChatInputProps = {
  handleInputChange: (e: ChangeEvent<HTMLInputElement>) => void;
  chatSubmit: (e: SyntheticEvent) => void;
  input: string;
  isFirstMessage: boolean;
  isShowingPredefinedPrompts: boolean;
  disabled?: boolean;
};

function EllaChatInput(props: EllaChatInputProps) {
  const {
    handleInputChange,
    chatSubmit,
    input,
    isFirstMessage,
    isShowingPredefinedPrompts,
    disabled = false
  } = props;

  const { metadata } = useEllaAnalytics();

  const handleSubmit = (event: SyntheticEvent) => {
    if (input.trim() === '') {
      event.preventDefault();
      return;
    }

    analytics.track('Ella AI User Input - Entered', {
      ...metadata,
      prompt: input
    });

    // If this is the first message, clear the input after submission in case of a ella preprompt override
    if (isFirstMessage) {
      // Create a proper synthetic event to clear the input
      const clearChatEvent = {
        target: { value: '' },
        currentTarget: { value: '' }
      } as ChangeEvent<HTMLInputElement>;

      // Clear the input field
      handleInputChange(clearChatEvent);
    }

    chatSubmit(event);
  };

  const placeholderCopy = useMemo(() => {
    if (disabled) return '';
    if (!isShowingPredefinedPrompts) return 'Ask another question';
    if (!isFirstMessage) return 'Or something else';
    return 'Or write your own response';
  }, [isFirstMessage, isShowingPredefinedPrompts, disabled]);

  return (
    <div className='EllaChatInput'>
      <form
        className='EllaChatInput-form'
        onSubmit={handleSubmit}
      >
        <input
          name='prompt'
          placeholder={placeholderCopy}
          value={input}
          onChange={handleInputChange}
          disabled={disabled}
        />

        <button
          aria-label='Submit'
          className='EllaChatInput-submit'
          type='submit'
          disabled={disabled}
        >
          <Image
            src={SubmitIcon}
            height={16}
            width={16}
            alt=''
            priority
          />
        </button>
      </form>
    </div>
  );
}

export default EllaChatInput;
